import { useEffect } from "react";

export interface NewsDetail {
  date: string;
  category: string;
  title: string;
  description: string;
  image: string;
  fullContent: string;
  stats: { icon: string; label: string; value: string }[];
  bulletPoints: string[];
}

interface NewsModalProps {
  news: NewsDetail | null;
  onClose: () => void;
}

export default function NewsModal({ news, onClose }: NewsModalProps) {
  useEffect(() => {
    if (news) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [news]);

  if (!news) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      <div
        className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image Header */}
        <div className="relative h-56 rounded-t-2xl overflow-hidden">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/30 hover:bg-black/50 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
          <span className="absolute top-4 left-4 bg-[#DC2626] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
            {news.category}
          </span>
          <div className="absolute bottom-4 left-6 right-6">
            <p className="text-white/70 text-xs mb-1 flex items-center space-x-1">
              <i className="ri-calendar-line"></i>
              <span>{news.date}</span>
            </p>
            <h2 className="text-xl font-bold text-white leading-snug">{news.title}</h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-8">
          <p className="text-gray-700 leading-relaxed mb-8 text-sm">{news.fullContent}</p>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {news.stats.map((s, i) => (
              <div key={i} className="bg-[#F8F9FA] rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <i className={`${s.icon} text-[#DC2626] text-lg`}></i>
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    {s.label}
                  </span>
                </div>
                <p className="text-[#1A365D] font-semibold text-sm">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Key Points */}
          <h3 className="text-[#1A365D] font-bold text-base mb-4">Key Highlights</h3>
          <ul className="space-y-3">
            {news.bulletPoints.map((point, idx) => (
              <li key={idx} className="flex items-start space-x-3 text-sm text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg flex-shrink-0 mt-0.5"></i>
                <span>{point}</span>
              </li>
            ))}
          </ul>


        </div>
      </div>
    </div>
  );
}
